import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Loader, Target, Lightbulb, CheckCircle, ChevronDown, ChevronUp, RotateCcw, BarChart3, Search, AlertTriangle, Info, Check, Plus } from 'lucide-react';
import GoalChart, { isChartable } from './GoalChart';
import { appConfig } from '../config/AppConfig';

const NextRow = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 2px; direction: rtl;
`;
const NextLabel = styled.div`
  display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: #006200;
  width: 100%;
`;
const NextChip = styled(motion.button)`
  padding: 7px 13px; border-radius: 999px; border: 1px solid rgba(27,140,60,0.3);
  background: rgba(27,140,60,0.06); color: #006200; font-size: 12.5px; font-weight: 600;
  cursor: pointer; text-align: right;
`;

/**
 * Goal Agent (Mode 5) result view: a running state, then the decision brief
 * (finding · recommendation · evidence) plus the sub-questions it investigated.
 * Presentational only — all data comes from the /goal-agent backend via useChat.
 */

const Wrap = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  direction: rtl;
  text-align: right;
`;

const Running = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px;
  border-radius: 12px;
  background: rgba(27,140,60, 0.08);
  border: 1px solid rgba(27,140,60, 0.25);
  color: #006200;
  font-weight: 600;
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Card = styled.div`
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const FindingCard = styled(Card)`
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%);
  border-color: rgba(27,140,60, 0.3);
`;

// Deterministic data-quality caveats — computed by the backend grounding stage and shown
// verbatim (not LLM-written), so the critical caveat is ALWAYS visible to the client.
const CaveatCard = styled(Card)`
  background: #fffbeb;
  border-color: #fde68a;
  padding: 13px 16px;
`;
const CaveatList = styled.ul`
  margin: 6px 0 0; padding: 0 18px 0 0;
  display: flex; flex-direction: column; gap: 5px;
  li { font-size: 12.5px; line-height: 1.6; color: #92400e; }
`;

// Honesty banner — what the descriptive data CAN and CANNOT establish for this goal (cause /
// how-to / forecast). From the planner; shown so the brief never silently swaps the question.
const AnswerabilityCard = styled(Card)`
  background: #eff6ff;
  border-color: #bfdbfe;
  padding: 12px 16px;
`;
const AnswerabilityText = styled.div`
  font-size: 12.5px; line-height: 1.65; color: #1e40af;
`;
// Connective reasoning — how the sub-questions fit together to answer the goal.
const ReasonCaption = styled.div`
  font-size: 12px; line-height: 1.6; color: #64748b; margin: 4px 2px 0;
  border-right: 2px solid #cbd5e1; padding-right: 9px;
`;
// Beta framing — honest "this is an evolving decision helper; verify before acting".
const BetaBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 999px;
  background: rgba(245,158,11,0.12); border: 1px solid rgba(245,158,11,0.4); color: #b45309;
  font-size: 11px; font-weight: 700;
`;
const BetaNote = styled.div`
  font-size: 11.5px; line-height: 1.6; color: #94a3b8; text-align: center;
  padding: 6px 4px 0; direction: rtl;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #006200;
  margin-bottom: 8px;
`;

const FindingText = styled.div`
  font-size: 16px;
  line-height: 1.7;
  color: #0f172a;
  font-weight: 600;
`;

const RecoText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
`;

const EvidenceList = styled.ul`
  margin: 0;
  padding: 0 18px 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  li { font-size: 13px; line-height: 1.6; color: #475569; }
`;

const SubToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 0;
`;

const SubItem = styled.div`
  border-top: 1px dashed #e2e8f0;
  padding: 10px 0;
  .q { font-size: 13px; font-weight: 600; color: #0f172a; }
  .a { font-size: 12.5px; color: #475569; margin-top: 4px; line-height: 1.6; }
  .sql { font-family: monospace; font-size: 11px; color: #94a3b8; margin-top: 4px; white-space: pre-wrap; word-break: break-word; }
`;

const ResetBtn = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

// ── live fan-out (streaming) ───────────────────────────────────────────────────
const Stage = styled.div`
  display: flex; align-items: center; gap: 9px; padding: 5px 0;
  font-size: 13.5px; font-weight: 600;
  color: ${p => p.$active ? '#006200' : p.$done ? '#334155' : '#94a3b8'};
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;
const PlanList = styled.div`
  display: flex; flex-direction: column; gap: 8px; margin: 8px 0;
`;
const PlanItem = styled(motion.div)`
  display: flex; align-items: flex-start; gap: 9px; padding: 9px 12px; border-radius: 10px;
  background: ${p => p.$done ? 'rgba(27,140,60,0.06)' : '#f8fafc'};
  border: 1px solid ${p => p.$done ? 'rgba(27,140,60,0.22)' : '#eef2f7'};
  .ic { flex-shrink: 0; margin-top: 1px; }
  .q { font-size: 13px; color: #0f172a; font-weight: 600; line-height: 1.5; }
  .why { font-size: 11.5px; color: #006200; margin-top: 3px; line-height: 1.5; }
  .a { font-size: 12px; color: #475569; margin-top: 4px; line-height: 1.55; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ── Approval / fork UI (the human reviews the verified plan before anything runs) ──────────────
const ApproveRow = styled.div`
  display: flex; gap: 13px; padding: 15px; border-radius: 13px; margin-bottom: 11px;
  border: 1.5px solid ${p => (p.$off ? '#e2e8f0' : p.$weak ? '#fcd34d' : '#e2e8f0')};
  background: ${p => (p.$off ? '#f8fafc' : p.$weak ? '#fffbeb' : '#fff')};
  opacity: ${p => (p.$off ? 0.72 : 1)}; transition: all .15s;
  .body { flex: 1; display: flex; flex-direction: column; gap: 7px; min-width: 0; }
  textarea { width: 100%; border: 1px solid #e2e8f0; border-radius: 9px; padding: 9px 12px;
    font-size: 14.5px; font-weight: 600; font-family: inherit; color: #0f172a; resize: vertical;
    direction: rtl; text-align: right; line-height: 1.55; background: #fff; }
  textarea:focus { outline: none; border-color: #1B8C3C; }
  .why { font-size: 12.5px; color: #006200; line-height: 1.55; }
  .why b { color: #475569; font-weight: 700; }
  .vgroup { display: flex; flex-direction: column; gap: 7px; padding: 9px 11px; border-radius: 9px;
    background: rgba(148,163,184,0.07); border: 1px dashed #cbd5e1; margin-top: 2px; }
  .vlabel { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 800;
    color: #64748b; letter-spacing: .2px; }
  .verdict { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 700; }
  .verdict.good { color: #15803d; }
  .verdict.weak { color: #b45309; }
  .suggest { align-self: flex-start; background: #fff; border: 1px solid rgba(27,140,60,0.45);
    color: #006200; font-size: 12.5px; font-weight: 600; border-radius: 9px; padding: 8px 12px; cursor: pointer;
    text-align: right; line-height: 1.5; }
  .suggest:hover { background: rgba(27,140,60,0.07); }
  .explore { align-self: flex-start; display: flex; align-items: center; gap: 5px; background: none; border: none;
    color: #94a3b8; font-size: 12px; font-weight: 600; cursor: pointer; padding: 2px 0; }
  .explore:hover { color: #006200; }
  .nudge-fb { display: flex; align-items: center; gap: 8px; font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
  .nudge-fb button { background: none; border: 1px solid #e2e8f0; border-radius: 7px; padding: 1px 7px; cursor: pointer; font-size: 13px; line-height: 1.6; }
  .nudge-fb button.on { border-color: #1B8C3C; background: rgba(27,140,60,0.08); }
  .nudge-fb .thx { color: #006200; font-weight: 600; }
`;
// Big, obvious include toggle (the old tiny checkbox made selection invisible).
const Toggle = styled.button`
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 9px; cursor: pointer; margin-top: 3px;
  display: flex; align-items: center; justify-content: center; transition: all .15s;
  border: 1.5px solid ${p => (p.$on ? '#1B8C3C' : '#cbd5e1')};
  background: ${p => (p.$on ? '#1B8C3C' : '#fff')}; color: #fff;
  &:hover { border-color: #1B8C3C; }
  svg { width: 17px; height: 17px; opacity: ${p => (p.$on ? 1 : 0)}; }
`;
const ApproveHelp = styled.div`
  font-size: 12.5px; color: #475569; margin-bottom: 14px; line-height: 1.65;
  background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 11px 13px;
  b { color: #006200; }
`;
const AddRow = styled.div`
  display: flex; gap: 8px; margin: 6px 0 14px;
  input { flex: 1; border: 1px solid #e2e8f0; border-radius: 9px; padding: 10px 12px; font-size: 13px;
    direction: rtl; text-align: right; font-family: inherit; }
  input:focus { outline: none; border-color: #1B8C3C; }
  button { border: none; border-radius: 9px; padding: 10px 16px; background: #f1f5f9; color: #475569;
    font-size: 13px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 5px; }
  button:disabled { opacity: 0.5; cursor: default; }
`;
const SynthBtn = styled(motion.button)`
  width: 100%; padding: 15px; border-radius: 12px; border: none; cursor: pointer;
  background: linear-gradient(135deg, #1B8C3C, #006200); color: #fff; font-size: 15px; font-weight: 800;
  display: flex; align-items: center; justify-content: center; gap: 9px;
  box-shadow: 0 6px 16px rgba(27,140,60,0.28);
  &:disabled { opacity: 0.45; cursor: default; box-shadow: none; }
`;

function GoalBrief({ goalStatus = 'done', plan = [], planRationales = [], planReasoning = '', planNote = '',
                    verdicts = [], answerabilityNote = '', subAnswers = [], brief, error,
                    onExplore, onExploreQuestion, onSynthesize, goal = '' }) {
  const [showSubs, setShowSubs] = useState(false);
  const answered = new Set((subAnswers || []).map(s => s.index));
  const running = goalStatus === 'planning' || goalStatus === 'running' || goalStatus === 'synthesizing';
  const awaiting = goalStatus === 'awaiting-approval';

  // ── Approval/fork local state — editable plan the human approves before anything runs ──
  const [items, setItems] = useState([]);
  const [newQ, setNewQ] = useState('');
  React.useEffect(() => {
    if (!awaiting) return;
    setItems((plan || []).map((q, i) => {
      const v = (verdicts || [])[i] || {};
      const weak = v.verdict === 'weak';
      return { text: q, include: !weak, verdict: weak ? 'weak' : 'good',
               reason: v.reason || '', suggestion: v.suggestion || '', why: (planRationales || [])[i] || '' };
    }));
    // re-init only when a fresh plan arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, (plan || []).join('||')]);

  const setItem = (i, patch) => setItems(p => p.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const applySuggestion = (i) => setItems(p => p.map((it, j) =>
    (j === i ? { ...it, text: it.suggestion, verdict: 'good', reason: '', suggestion: '', include: true } : it)));
  const addOwn = () => { if (!newQ.trim()) return;
    setItems(p => [...p, { text: newQ.trim(), include: true, verdict: 'good', reason: '', suggestion: '', why: '' }]);
    setNewQ(''); };
  const includedCount = items.filter(it => it.include && it.text.trim()).length;
  // One-tap feedback on a nudge → stored for offline tuning (NOT self-learning). Fire-and-forget.
  const sendNudgeFeedback = (it, agrees) => {
    try {
      fetch(`${appConfig.getMainApiUrl()}/goal-agent/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goal, question: it.text, verdict: it.verdict,
                               reason: it.reason || '', agrees }),
      }).catch(() => {});
    } catch (_) { /* never block the UI on feedback */ }
  };
  const anyWeak = items.some(it => it.verdict === 'weak');
  const doSynth = () => {
    const qs = items.filter(it => it.include && it.text.trim()).map(it => it.text.trim());
    if (qs.length && onSynthesize) onSynthesize(qs);
  };

  return (
    <Wrap initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      {/* Live fan-out — streamed stage by stage */}
      {running && (
        <Card>
          <SectionLabel><Target size={15} /> تحليل القرار</SectionLabel>

          <Stage $active={goalStatus === 'planning'} $done={goalStatus !== 'planning'}>
            {goalStatus === 'planning'
              ? <Loader size={15} className="spin" />
              : <CheckCircle size={15} color="#1B8C3C" />}
            تحديد الأسئلة الفرعية
          </Stage>

          {plan.length > 0 && (
            <PlanList>
              {plan.map((q, i) => {
                const done = answered.has(i);
                const sa = (subAnswers || []).find(s => s.index === i);
                const ans = sa && sa.answer ? sa.answer : '';
                return (
                  <PlanItem key={i} $done={done} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}>
                    <span className="ic">
                      {done ? <CheckCircle size={15} color="#1B8C3C" /> : <Loader size={15} className="spin" color="#94a3b8" />}
                    </span>
                    <div>
                      <div className="q">{q}</div>
                      {planRationales[i] && <div className="why">{planRationales[i]}</div>}
                      {done && ans && <div className="a">{ans.length > 180 ? ans.slice(0, 180) + '…' : ans}</div>}
                    </div>
                  </PlanItem>
                );
              })}
            </PlanList>
          )}

          {planReasoning && <ReasonCaption>{planReasoning}</ReasonCaption>}

          <Stage $active={goalStatus === 'synthesizing'}>
            {goalStatus === 'synthesizing'
              ? <Loader size={15} className="spin" />
              : <span style={{ width: 15, display: 'inline-block' }} />}
            دمج النتائج في خلاصة قرار
          </Stage>
        </Card>
      )}

      {/* The FORK — review the verified plan, edit/explore, then opt in to synthesis */}
      {awaiting && (
        <Card>
          <SectionLabel style={{ fontSize: 15 }}><Target size={17} /> راجِع الأسئلة، ثم ادمجها في خلاصة</SectionLabel>
          <ApproveHelp>
            لتحليل هدفك، اقترحنا الأسئلة التالية وقيّمنا جودة كلٍّ منها. <b>الأخضر = مُختار وسيُحلَّل</b>،
            والرمادي مُستبعد — اضغط المربّع لتبديل الاختيار. يمكنك تعديل أي سؤال، أو استبدال الضعيف بالأفضل،
            أو استكشاف سؤال وحده. ثم اضغط <b>«ادمج في خلاصة»</b> بالأسفل.
          </ApproveHelp>

          {items.map((it, i) => (
            <ApproveRow key={i} $weak={it.verdict === 'weak'} $off={!it.include}>
              <Toggle $on={it.include} onClick={() => setItem(i, { include: !it.include })}
                      title={it.include ? 'مُختار — اضغط للاستبعاد' : 'مُستبعد — اضغط للاختيار'}>
                <Check />
              </Toggle>
              <div className="body">
                <textarea value={it.text} rows={1} onChange={e => setItem(i, { text: e.target.value })} />
                {it.why && <div className="why"><b>الغرض:</b> {it.why}</div>}
                {/* NUDGE, not a verdict: only flag the ones worth a second look — never certify "good"
                    (the design forbids a ✓ badge). Good questions appear neutral, included by default. */}
                {it.verdict === 'weak' && (
                  <div className="vgroup">
                    <div className="vlabel"><Info size={12} /> نقطة للانتباه — اقتراح، والقرار لك</div>
                    <div className="verdict weak">
                      <AlertTriangle size={13} />
                      {it.reason || 'قد يحتاج هذا السؤال إلى تحقّق قبل اعتماده'}
                    </div>
                    {it.suggestion && (
                      <button className="suggest" onClick={() => applySuggestion(i)}>
                        ↺ بديل أفضل: {it.suggestion}
                      </button>
                    )}
                  </div>
                )}
                {/* Per-question feedback on EVERY suggestion (good or weak) — local signal that tunes the
                    planner: did the user find THIS question useful? Stored per-question for the analysts. */}
                <div className="nudge-fb">
                  <span>هل هذا السؤال مفيد؟</span>
                  <button className={it.fb === 'up' ? 'on' : ''}
                          onClick={() => { sendNudgeFeedback(it, true); setItem(i, { fb: 'up' }); }}>👍</button>
                  <button className={it.fb === 'down' ? 'on' : ''}
                          onClick={() => { sendNudgeFeedback(it, false); setItem(i, { fb: 'down' }); }}>👎</button>
                  {it.fb && <span className="thx">شكراً</span>}
                </div>
                {onExploreQuestion && (
                  <button className="explore" onClick={() => onExploreQuestion(it.text)}>
                    <Search size={12} /> استكشف هذا السؤال وحده
                  </button>
                )}
              </div>
            </ApproveRow>
          ))}

          <AddRow>
            <input value={newQ} placeholder="أضف سؤالك الخاص…" onChange={e => setNewQ(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter') addOwn(); }} />
            <button onClick={addOwn} disabled={!newQ.trim()}><Plus size={14} /> أضف</button>
          </AddRow>

          {/* The verifier's INDEPENDENT plan-level assessment (zoom-out): do these together answer the
              goal? Shown instead of the planner's self-reasoning. Feedback is per-question (above). */}
          <div style={{ padding: '10px 12px', margin: '2px 0 12px', borderRadius: 10,
                        background: 'rgba(27,140,60,0.06)', border: '1px solid rgba(27,140,60,0.22)',
                        fontSize: 12.5, color: '#006200', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700 }}>
              {anyWeak ? '✓ راجعنا الأسئلة وأبرزنا ما يستحقّ الانتباه'
                       : '✓ راجعنا الأسئلة — لا ملاحظات تُذكر'}
            </div>
            {planNote && <div style={{ fontWeight: 400, marginTop: 3 }}>{planNote}</div>}
          </div>

          {answerabilityNote && (
            <AnswerabilityCard style={{ marginBottom: 14 }}>
              <SectionLabel style={{ color: '#1d4ed8' }}>
                <Info size={15} /> ما تُظهره البيانات وما لا تُظهره
              </SectionLabel>
              <AnswerabilityText>{answerabilityNote}</AnswerabilityText>
            </AnswerabilityCard>
          )}

          <SynthBtn onClick={doSynth} disabled={includedCount === 0}
                    whileHover={{ scale: includedCount ? 1.01 : 1 }} whileTap={{ scale: includedCount ? 0.99 : 1 }}>
            <Target size={18} /> ادمج {includedCount > 0 ? `${includedCount} ` : ''}أسئلة في خلاصة قرار
          </SynthBtn>
          <BetaNote>🧪 نسخة تجريبية — أنت تختار الأسئلة؛ الخلاصة تُبنى مما اخترته فقط.</BetaNote>
        </Card>
      )}

      {goalStatus === 'error' && (
        <Card style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
          <div style={{ color: '#b91c1c', fontWeight: 600 }}>
            تعذّر تحليل الهدف{error ? ` — ${error}` : '.'}
          </div>
        </Card>
      )}

      {/* The decision brief — REASONING FIRST, conclusion last (the answer follows from the logic) */}
      {brief && (
        <>
          {brief.reasoning && (
            <div style={{ padding: '11px 13px', marginBottom: 11, borderRadius: 12,
                          background: 'rgba(148,163,184,0.08)', border: '1px solid #e2e8f0',
                          fontSize: 13, color: '#475569', lineHeight: 1.65 }}>
              <div style={{ fontWeight: 700, color: '#64748b', fontSize: 11.5, marginBottom: 3 }}>
                كيف تتكامل الإجابات للوصول للخلاصة
              </div>
              {brief.reasoning}
            </div>
          )}
          <FindingCard>
            <SectionLabel>
              <Target size={15} /> الخلاصة
              <BetaBadge style={{ marginInlineStart: 'auto' }}>🧪 تجريبي</BetaBadge>
            </SectionLabel>
            <FindingText>{brief.finding}</FindingText>
          </FindingCard>

          {/* Honesty banner: what the data can / cannot establish (cause, how-to, forecast) */}
          {answerabilityNote && (
            <AnswerabilityCard>
              <SectionLabel style={{ color: '#1d4ed8' }}>
                <Info size={15} /> ما تُظهره البيانات وما لا تُظهره
              </SectionLabel>
              <AnswerabilityText>{answerabilityNote}</AnswerabilityText>
            </AnswerabilityCard>
          )}

          {/* Deterministic data-quality caveats (always shown when present, LLM-independent) */}
          {Array.isArray(brief.caveats) && brief.caveats.length > 0 && (
            <CaveatCard>
              <SectionLabel style={{ color: '#b45309' }}>
                <AlertTriangle size={15} /> ملاحظات على جودة البيانات
              </SectionLabel>
              <CaveatList>
                {brief.caveats.map((c, i) => <li key={i}>{c}</li>)}
              </CaveatList>
            </CaveatCard>
          )}

          {/* Headline chart: the first chartable breakdown, shown big as visual proof. */}
          {(() => {
            const headline = (subAnswers || []).find(s => isChartable(s.columnNames, s.results));
            return headline ? (
              <Card>
                <SectionLabel><BarChart3 size={15} /> {headline.question}</SectionLabel>
                <GoalChart columnNames={headline.columnNames} results={headline.results} big />
              </Card>
            ) : null;
          })()}

          {brief.recommendation && (
            <Card>
              <SectionLabel><Lightbulb size={15} /> التوصية</SectionLabel>
              <RecoText>{brief.recommendation}</RecoText>
            </Card>
          )}

          {Array.isArray(brief.evidence) && brief.evidence.length > 0 && (
            <Card>
              <SectionLabel><CheckCircle size={15} /> الأدلة</SectionLabel>
              <EvidenceList>
                {brief.evidence.map((e, i) => <li key={i}>{e}</li>)}
              </EvidenceList>
            </Card>
          )}

          {/* Drill-from-brief: follow-up chips that run a normal query (so the user isn't stuck) */}
          {onExplore && Array.isArray(brief.next_questions) && brief.next_questions.length > 0 && (
            <NextRow>
              <NextLabel><Search size={13} /> تعمّق أكثر</NextLabel>
              {brief.next_questions.map((q, i) => (
                <NextChip key={i} onClick={() => onExplore(q)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  {q}
                </NextChip>
              ))}
            </NextRow>
          )}

          {subAnswers.length > 0 && (
            <Card>
              <SubToggle onClick={() => setShowSubs(v => !v)}>
                {showSubs ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                الاستعلامات التي تم تحليلها ({subAnswers.length})
              </SubToggle>
              {showSubs && subAnswers.map((s, i) => (
                <SubItem key={i}>
                  <div className="q">{s.question}</div>
                  {isChartable(s.columnNames, s.results)
                    ? <GoalChart columnNames={s.columnNames} results={s.results} />
                    : (s.answer && <div className="a">{s.answer}</div>)}
                  {s.sql && <div className="sql">{s.sql}</div>}
                </SubItem>
              ))}
            </Card>
          )}

          <BetaNote>
            🧪 نسخة تجريبية من محلّل القرار — راجِع الأدلة والاستعلامات، ولا تَبنِ قراراً نهائياً قبل التحقّق من بياناتك.
          </BetaNote>
        </>
      )}
    </Wrap>
  );
}

export default GoalBrief;
